import { ServiceId } from 'hooks/useQueryFilter';
import { useTranslation } from 'react-i18next';
import { useToastNotify } from '@opswat/ui';
import { axios, MutationFetcher, SWRConfiguration, SWRKey, useSWRImmutable, useSWRMutation } from 'services/swr';
import { useUser } from 'services/User';

export interface IntegrationItem {
    id: string;
    serviceId: ServiceId;
    serviceApikey: string;
    createdAt: string;
    updatedAt: string;
}

export const useIntegrationList = (options: SWRConfiguration<{ data: IntegrationItem[] }> = {}) => {
    const { user } = useUser();
    const cacheKey: SWRKey =
        user?.portal_api_key && user?.isLoggedIn
            ? { url: '/api/integrations', baseURL: process.env.GATSBY_SITE_URL }
            : null;

    return useSWRImmutable<{ data: IntegrationItem[] }>(cacheKey, { shouldRetryOnError: false, ...options });
};

export const useCreateIntegration = () => {
    const { t } = useTranslation();
    const { toastNotify } = useToastNotify();
    const cacheKey: SWRKey = { url: '/api/integrations', baseURL: process.env.GATSBY_SITE_URL };

    const createIntegration: MutationFetcher<IntegrationItem, SWRKey, { serviceId: string }> = async (
        { url, ...config },
        { arg: data },
    ) => axios.post(url, data, config).then((res) => res.data);

    return useSWRMutation(cacheKey, createIntegration, {
        revalidate: false,
        onError: () =>
            toastNotify({
                title: t('Error'),
                message: t('Failed to create integration. Please try again.'),
                type: 'error',
                hasClose: true,
                duration: 4000,
            }),
    });
};

export const useIntegration = ({ serviceId }: { serviceId: ServiceId | '' }) => {
    const { trigger: createIntegration, isMutating } = useCreateIntegration();
    const {
        data: integrationList,
        isLoading: isLoadingIntegrationList,
        mutate,
    } = useIntegrationList({
        onSuccess: (data) => {
            // Fire-and-forget: onSuccess expects a void return, so run the async
            // auto-provisioning in a discarded IIFE rather than returning a Promise.
            void (async () => {
                const integrations = data?.data ?? [];
                const integration = integrations.find((item) => item.serviceId === serviceId);
                if (serviceId && !integration) {
                    try {
                        await createIntegration({ serviceId });
                        await mutate();
                    } catch {
                        // do nothing
                    }
                }
            })();
        },
    });

    const integrations = integrationList?.data ?? [];
    const integration = integrations.find((item) => item.serviceId === serviceId);
    const cacheKey: SWRKey = integration
        ? { url: `/api/integrations/${integration.id}`, baseURL: process.env.GATSBY_SITE_URL }
        : null;

    const swrData = useSWRImmutable<IntegrationItem>(cacheKey);

    return {
        ...swrData,
        integration,
        isLoading: isLoadingIntegrationList || swrData.isLoading || isMutating,
    };
};

export const useDeleteIntegration = () => {
    const cacheKey: SWRKey = { url: '/api/integrations/', baseURL: process.env.GATSBY_SITE_URL };

    const deleteIntegration: MutationFetcher<IntegrationItem, SWRKey, string> = async (
        { url, ...config },
        { arg: id },
    ) => axios.delete(url + id, config).then((res) => res.data);

    return useSWRMutation(cacheKey, deleteIntegration, { revalidate: false });
};
